/**
 * Every finding of the run: toolbar (search, filters, group by, sort, columns, density, CSV export) and the table.
 * Groups render as one <tbody> each with a row-group header. Sortable headers carry aria-sort.
 */
import { Badge, Button, Card, CardHead, Select, Text, TextInput } from '@qm/ui';
import { GROUP_BY, SORT_FIELDS, type AdvancedFinding, type AdvancedReport } from '@qm/contracts/audit-remediation';
import {
  activeFilterCount, ALL_COLUMNS, FILTER_DIMENSIONS, FILTER_KEYS, groupFindings, visibleFindings,
  type Column, type FilterDimension, type GroupBy, type SortField,
} from './model';
import type { ReportStore, ReportUiState } from './store';
import { L, useReportText } from './text';
import { resolvabilityTone, severityTone, statusTone } from './tones';

const SORTABLE = new Set<string>(SORT_FIELDS);

/** i18n key for a value of a filter dimension / group. */
export function valueKey(dim: FilterDimension | Exclude<GroupBy, 'none'>, v: string): string {
  switch (dim) {
    case 'severity': return `audit-remediation.severity.${v}`;
    case 'signal': return `audit-remediation.report.signal.${v}`;
    case 'status': return `audit-remediation.status.${v}`;
    case 'source': return `audit-remediation.source.${v}`;
    case 'owner': return `audit-remediation.report.owner.${v}`;
    case 'resolvability': return `audit-remediation.resolvability.${v}`;
    case 'roadmapSlot': return `audit-remediation.roadmap.${v}`;
  }
}

export function FindingsSection({ report, state, store, onExport }: { report: AdvancedReport; state: ReportUiState; store: ReportStore; onExport: () => void }) {
  const { t, num } = useReportText();
  const def = state.def;
  const rows = visibleFindings(report, def);
  const groups = groupFindings(rows, def.groupBy);
  const filters = activeFilterCount(def.filters);
  const cols = ALL_COLUMNS.filter((c) => def.columns.includes(c));

  return (
    <Card aria-labelledby="ar-findings" className="qm-ar-findings" data-density={state.density}>
      <CardHead id="ar-findings" title={t('audit-remediation.report.findings.title')}
        sub={t('audit-remediation.report.findings.showing', { shown: num(rows.length), total: num(report.findings.length) })}
        trailing={<Button className="qm-ar-noprint" onClick={onExport}>{t('audit-remediation.report.export.csv')}</Button>} />

      <div className="qm-ar-toolbar qm-ar-noprint" role="toolbar" aria-label={t('audit-remediation.report.toolbar')}>
        <div className="qm-ar-toolbar__search">
          <label className="qm-field__label" htmlFor="ar-search">{t('audit-remediation.report.filter.search')}</label>
          <TextInput id="ar-search" type="search" value={def.filters.search ?? ''} placeholder={t('audit-remediation.report.filter.searchHint')}
            onChange={(e) => store.setFilter('search', e.currentTarget.value || null)} />
        </div>

        <details className="qm-ar-pop">
          <summary>{t('audit-remediation.report.filter.title')}{filters ? ` (${num(filters)})` : ''}</summary>
          <div className="qm-ar-pop__body qm-ar-filters">
            {FILTER_KEYS.map((k) => (
              <fieldset key={k}>
                <legend>{t(`audit-remediation.report.dim.${k}`)}</legend>
                {(FILTER_DIMENSIONS[k] as readonly string[]).map((v) => {
                  const id = `ar-f-${k}-${v}`;
                  return (
                    <label key={v} htmlFor={id} className="qm-ar-check">
                      <input id={id} type="checkbox" checked={(def.filters[k] as readonly string[]).includes(v)} onChange={() => store.toggleFilter(k, v)} />
                      {t(valueKey(k, v))}
                    </label>
                  );
                })}
              </fieldset>
            ))}
            <Button size="sm" variant="ghost" onClick={() => store.clearFilters()} disabled={!filters}>{t('audit-remediation.report.filter.clear')}</Button>
          </div>
        </details>

        <div className="qm-ar-toolbar__field">
          <label className="qm-field__label" htmlFor="ar-group">{t('audit-remediation.report.groupBy')}</label>
          <Select id="ar-group" value={def.groupBy} onChange={(e) => store.setGroupBy(e.currentTarget.value as GroupBy)}>
            {GROUP_BY.map((g) => <option key={g} value={g}>{t(g === 'none' ? 'audit-remediation.report.group.none' : `audit-remediation.report.dim.${g}`)}</option>)}
          </Select>
        </div>

        <div className="qm-ar-toolbar__field">
          <label className="qm-field__label" htmlFor="ar-sort">{t('audit-remediation.report.sortBy')}</label>
          <div className="qm-ar-inline">
            <Select id="ar-sort" value={def.sort.field} onChange={(e) => store.setSort(e.currentTarget.value as SortField, def.sort.dir)}>
              {SORT_FIELDS.map((s) => <option key={s} value={s}>{t(`audit-remediation.report.col.${s}`)}</option>)}
            </Select>
            <Button size="sm" aria-label={t('audit-remediation.report.sortDir', { dir: t(`audit-remediation.report.dir.${def.sort.dir}`) })}
              onClick={() => store.setSort(def.sort.field, def.sort.dir === 'asc' ? 'desc' : 'asc')}>
              {t(`audit-remediation.report.dir.${def.sort.dir}`)}
            </Button>
          </div>
        </div>

        <details className="qm-ar-pop">
          <summary>{t('audit-remediation.report.columns')}</summary>
          <fieldset className="qm-ar-pop__body">
            <legend className="qm-sr-only">{t('audit-remediation.report.columns')}</legend>
            {ALL_COLUMNS.map((c) => {
              const id = `ar-col-${c}`;
              return (
                <label key={c} htmlFor={id} className="qm-ar-check">
                  <input id={id} type="checkbox" checked={def.columns.includes(c)} disabled={def.columns.length === 1 && def.columns.includes(c)} onChange={() => store.toggleColumn(c)} />
                  {t(`audit-remediation.report.col.${c}`)}
                </label>
              );
            })}
          </fieldset>
        </details>

        <div className="qm-ar-toolbar__field">
          <label className="qm-field__label" htmlFor="ar-density">{t('audit-remediation.report.density')}</label>
          <Select id="ar-density" value={state.density} onChange={(e) => store.setDensity(e.currentTarget.value as 'comfortable' | 'compact')}>
            <option value="comfortable">{t('audit-remediation.report.density.comfortable')}</option>
            <option value="compact">{t('audit-remediation.report.density.compact')}</option>
          </Select>
        </div>
      </div>

      {!rows.length ? (
        <div className="qm-ar-empty" role="status">
          <Text>{t(report.findings.length ? 'audit-remediation.report.findings.noMatch' : 'audit-remediation.report.findings.none')}</Text>
          {filters ? <Button size="sm" onClick={() => store.clearFilters()}>{t('audit-remediation.report.filter.clear')}</Button> : null}
        </div>
      ) : (
        <div className="qm-table-wrap">
          <table className="qm-table qm-ar-table">
            <caption className="qm-sr-only">{t('audit-remediation.report.findings.title')}</caption>
            <thead>
              <tr>
                <SortTh field="title" state={state} store={store} label={t('audit-remediation.report.col.title')} />
                {cols.map((c) => (SORTABLE.has(c)
                  ? <SortTh key={c} field={c as SortField} state={state} store={store} label={t(`audit-remediation.report.col.${c}`)} numeric={c === 'priority'} />
                  : <th key={c} scope="col">{t(`audit-remediation.report.col.${c}`)}</th>))}
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g.key || 'all'}>
                {def.groupBy !== 'none' ? (
                  <tr className="qm-ar-group"><th scope="rowgroup" colSpan={cols.length + 1}>{t(valueKey(def.groupBy as Exclude<GroupBy, 'none'>, g.key))} <span className="qm-ar-muted">({num(g.items.length)})</span></th></tr>
                ) : null}
                {g.items.map((f) => <Row key={f.id} f={f} cols={cols} selected={state.selectedId === f.id} onOpen={() => store.select(f.id)} />)}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </Card>
  );
}

function SortTh({ field, label, state, store, numeric }: { field: SortField; label: string; state: ReportUiState; store: ReportStore; numeric?: boolean }) {
  const { t } = useReportText();
  const active = state.def.sort.field === field;
  const sort = active ? (state.def.sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th scope="col" aria-sort={sort} data-numeric={numeric ? 'true' : undefined}>
      <button type="button" className="qm-ar-sort" onClick={() => store.setSort(field)}
        aria-label={t('audit-remediation.report.sortByCol', { col: label })}>
        {label}<span aria-hidden="true">{active ? (state.def.sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}</span>
      </button>
    </th>
  );
}

function Row({ f, cols, selected, onOpen }: { f: AdvancedFinding; cols: Column[]; selected: boolean; onOpen: () => void }) {
  const { t, num } = useReportText();
  const cell = (c: Column) => {
    switch (c) {
      case 'priority': return <span className="qm-ar-prio">{num(f.priority)}</span>;
      case 'severity': return <Badge tone={severityTone(f.severity)}>{t(`audit-remediation.severity.${f.severity}`)}</Badge>;
      case 'signal': return t(`audit-remediation.report.signal.${f.signal}`);
      case 'source': return t(`audit-remediation.source.${f.source}`);
      case 'status': return <Badge tone={statusTone(f.status)}>{t(`audit-remediation.status.${f.status}`)}</Badge>;
      case 'resolvability': return <ResolvabilityBadge f={f} />;
      case 'owner': return t(`audit-remediation.report.owner.${f.owner}`);
      case 'effort': return t(`audit-remediation.report.effort.${f.effort}`);
      case 'roadmapSlot': return t(`audit-remediation.roadmap.${f.roadmapSlot}`);
      case 'checkIds': return <code dir="ltr">{f.checkIds.join(', ')}</code>;
    }
  };
  return (
    <tr data-selected={selected ? 'true' : undefined} data-status={f.status}>
      <th scope="row" data-label={t('audit-remediation.report.col.title')}>
        <button type="button" className="qm-ar-link" aria-haspopup="dialog" aria-expanded={selected} onClick={onOpen}><L v={f.title} /></button>
        {f.regulatory ? <span className="qm-ar-flag">{t('audit-remediation.report.regulatory')}</span> : null}
      </th>
      {cols.map((c) => <td key={c} data-label={t(`audit-remediation.report.col.${c}`)} data-numeric={c === 'priority' ? 'true' : undefined}>{cell(c)}</td>)}
    </tr>
  );
}

export function ResolvabilityBadge({ f }: { f: AdvancedFinding }) {
  const { t } = useReportText();
  const m = f.resolvability.mode;
  const hint = m !== 'auto' && f.resolvability.provider
    ? t('audit-remediation.resolvabilityHint.connect', { provider: t(`audit-remediation.provider.${f.resolvability.provider}`) })
    : t(`audit-remediation.resolvabilityHint.${m}`);
  return <span className="qm-ar-resolve" data-mode={m} title={hint}><Badge tone={resolvabilityTone(m)}>{t(`audit-remediation.resolvability.${m}`)}</Badge></span>;
}
